import Copyright from '../components/Copyright';
import React, { useState, useEffect, useCallback } from 'react';
import { exercisesAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, startOfWeek, addDays } from 'date-fns';

// ─── Plan Page Container ───────────────────────────────────────────────────────

export default function PlanPage() {
  const [tab, setTab] = useState('library');
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">PLAN</h1>
        <div className="tabs">
          {[['library','📚 Library'],['plans','🏋️ My Plans'],['calendar','📅 Calendar']].map(([k,l]) => (
            <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="page-body">
        {tab === 'library' && <LibraryTab />}
        {tab === 'plans' && <PlansTab />}
        {tab === 'calendar' && <CalendarTab />}
      </div>
    </div>
  );
}

// ─── Library Tab ───────────────────────────────────────────────────────────────

function LibraryTab() {
  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState({ muscles: [], equipment: [], categories: [], levels: [] });
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({ muscle: '', equipment: '', category: '', level: '' });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [customExercises, setCustomExercises] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [editingCustom, setEditingCustom] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    exercisesAPI.getFilters().then(r => setFilters(r.data));
    exercisesAPI.getCustomExercises().then(r => setCustomExercises(r.data));
    exercisesAPI.getPlans().then(r => setPlans(r.data));
  }, []);

  const search = useCallback(async (reset = true) => {
    setLoading(true);
    const o = reset ? 0 : offset;
    if (reset) setOffset(0);
    try {
      const res = await exercisesAPI.search({
        q: query || undefined,
        muscle: selected.muscle || undefined,
        equipment: selected.equipment || undefined,
        category: selected.category || undefined,
        level: selected.level || undefined,
        limit: 20, offset: o
      });
      setExercises(reset ? res.data.exercises : (p => [...p, ...res.data.exercises]));
      setTotal(res.data.total);
      if (!reset) setOffset(o + 20);
    } finally { setLoading(false); }
  }, [query, selected, offset]);

  useEffect(() => { search(true); }, [query, selected.muscle, selected.equipment, selected.category, selected.level]);

  const addToPlan = async (planId, exerciseId, customName) => {
    try {
      const plan = plans.find(p => p.id === planId);
      const planDetail = await exercisesAPI.getPlan(planId);
      const existing = planDetail.data.exercises.map(e => ({
        exercise_library_id: e.exercise_library_id,
        custom_exercise_name: e.custom_exercise_name,
        target_sets: e.target_sets, target_reps: e.target_reps, target_weight: e.target_weight
      }));
      const newEx = exerciseId
        ? { exercise_library_id: exerciseId }
        : { custom_exercise_name: customName };
      await exercisesAPI.updatePlan(planId, { name: plan.name, exercises: [...existing, newEx] });
      toast.success(`Added to ${plan.name}!`);
    } catch { toast.error('Failed to add to plan'); }
  };

  const deleteCustom = async (id) => {
    if (!window.confirm('Delete this exercise?')) return;
    await exercisesAPI.deleteCustomExercise(id);
    setCustomExercises(p => p.filter(e => e.id !== id));
    toast.success('Deleted');
  };

  if (detail) return (
    <ExerciseDetail exercise={detail} onBack={() => setDetail(null)}
      plans={plans} onAddToPlan={addToPlan} />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Custom exercises */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="section-title" style={{ margin: 0 }}>
            ⭐ My Custom Exercises
            {customExercises.length > 0 && <span className="badge badge-purple" style={{ marginLeft: 8 }}>{customExercises.length}</span>}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => { setEditingCustom(null); setShowCustomForm(true); }}>
            + Create
          </button>
        </div>
        {customExercises.length === 0 ? (
          <p style={{ color: 'var(--text-3)', fontSize: 14 }}>No custom exercises yet. Create one to get started.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {customExercises.map(ex => (
              <div key={ex.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', background: 'var(--bg-3)', borderRadius: 'var(--radius)' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{ex.name}</span>
                  <span className="badge badge-custom" style={{ marginLeft: 8 }}>Custom</span>
                  {ex.category && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-3)' }}>{ex.category}</span>}
                  {ex.primary_muscles?.length > 0 && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-3)' }}>
                      {ex.primary_muscles.join(', ')}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select className="input" style={{ width: 'auto', fontSize: 12, padding: '4px 8px' }}
                    onChange={e => { if (e.target.value) { addToPlan(parseInt(e.target.value), null, ex.name); e.target.value = ''; } }}>
                    <option value="">+ Plan</option>
                    {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditingCustom(ex); setShowCustomForm(true); }}>✏️</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteCustom(ex.id)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input className="input" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search 800+ exercises..." style={{ flex: 1, minWidth: 200 }} />
        {[
          ['muscle', filters.muscles, 'Muscle'],
          ['equipment', filters.equipment, 'Equipment'],
          ['category', filters.categories, 'Category'],
          ['level', filters.levels, 'Level'],
        ].map(([k, opts, label]) => (
          <select key={k} className="input" style={{ width: 'auto' }}
            value={selected[k]} onChange={e => setSelected(p => ({ ...p, [k]: e.target.value }))}>
            <option value="">{label}</option>
            {opts.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
          </select>
        ))}
        {Object.values(selected).some(Boolean) && (
          <button className="btn btn-ghost btn-sm"
            onClick={() => setSelected({ muscle: '', equipment: '', category: '', level: '' })}>
            Clear filters
          </button>
        )}
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-3)' }}>{total} exercises found</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {exercises.map(ex => (
          <div key={ex.id} className="card card-sm" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
            onClick={() => setDetail(ex)}>
            {ex.images?.[0] ? (
              <img src={ex.images[0]} alt={ex.name}
                style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--bg-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>🏋️</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{ex.name}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ex.category && <span className="badge badge-blue">{ex.category}</span>}
                {ex.equipment && <span className="badge badge-amber">{ex.equipment}</span>}
                {ex.level && <span className="badge badge-green">{ex.level}</span>}
              </div>
              {ex.primary_muscles?.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                  {ex.primary_muscles.join(', ')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
              <select className="input" style={{ width: 'auto', fontSize: 12, padding: '4px 8px' }}
                onChange={e => { if (e.target.value) { addToPlan(parseInt(e.target.value), ex.id); e.target.value = ''; } }}>
                <option value="">+ Plan</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      {exercises.length < total && (
        <button className="btn btn-secondary btn-full" onClick={() => search(false)} disabled={loading}>
          {loading ? 'Loading...' : 'Load more'}
        </button>
      )}

      {showCustomForm && (
        <CustomExerciseModal exercise={editingCustom}
          onClose={() => setShowCustomForm(false)}
          onSave={async (data) => {
            if (editingCustom) {
              const res = await exercisesAPI.updateCustomExercise(editingCustom.id, data);
              setCustomExercises(p => p.map(e => e.id === editingCustom.id ? res.data : e));
            } else {
              const res = await exercisesAPI.createCustomExercise(data);
              setCustomExercises(p => [...p, res.data]);
            }
            setShowCustomForm(false);
            toast.success(editingCustom ? 'Updated!' : 'Created!');
          }} />
      )}
    </div>
  );
}

function ExerciseDetail({ exercise, onBack, plans, onAddToPlan }) {
  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>← Back to Library</button>
      <div className="card">
        <h2 style={{ marginBottom: 12 }}>{exercise.name}</h2>
        {exercise.images?.length >= 2 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {exercise.images.slice(0, 2).map((img, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={img} alt={i === 0 ? 'Start' : 'End'}
                  style={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 'var(--radius)' }} />
                <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.7)',
                  padding: '2px 8px', borderRadius: 4, fontSize: 12, color: 'white' }}>
                  {i === 0 ? 'Start' : 'End'}
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {exercise.category && <span className="badge badge-blue">{exercise.category}</span>}
          {exercise.level && <span className="badge badge-green">{exercise.level}</span>}
          {exercise.equipment && <span className="badge badge-amber">{exercise.equipment}</span>}
          {exercise.force && <span className="badge badge-purple">{exercise.force}</span>}
        </div>
        {exercise.primary_muscles?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>PRIMARY MUSCLES</div>
            <p>{exercise.primary_muscles.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')}</p>
          </div>
        )}
        {exercise.secondary_muscles?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>SECONDARY MUSCLES</div>
            <p style={{ color: 'var(--text-2)' }}>{exercise.secondary_muscles.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(', ')}</p>
          </div>
        )}
        {exercise.instructions?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>INSTRUCTIONS</div>
            <ol style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {exercise.instructions.map((step, i) => (
                <li key={i} style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>{step}</li>
              ))}
            </ol>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {plans.map(plan => (
            <button key={plan.id} className="btn btn-secondary btn-sm"
              onClick={() => onAddToPlan(plan.id, exercise.id)}>
              + {plan.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CustomExerciseModal({ exercise, onClose, onSave }) {
  const [form, setForm] = useState({
    name: exercise?.name || '',
    category: exercise?.category || '',
    equipment: exercise?.equipment || '',
    primary_muscles: exercise?.primary_muscles?.join(', ') || '',
    instructions: exercise?.instructions?.join('\n') || '',
    notes: exercise?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setLoading(true);
    try {
      await onSave({
        name: form.name.trim(),
        category: form.category || undefined,
        equipment: form.equipment || undefined,
        primary_muscles: form.primary_muscles.split(',').map(s => s.trim()).filter(Boolean),
        instructions: form.instructions.split('\n').map(s => s.trim()).filter(Boolean),
        notes: form.notes || undefined,
      });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: '100%', maxWidth: 500 }}>
        <h3 style={{ marginBottom: 16 }}>{exercise ? 'Edit Exercise' : 'Create Custom Exercise'}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="input-group"><label className="input-label">Name *</label>
            <input className="input" value={form.name} onChange={set('name')} /></div>
          <div className="input-row">
            <div className="input-group"><label className="input-label">Category</label>
              <input className="input" value={form.category} onChange={set('category')} placeholder="e.g. Strength" /></div>
            <div className="input-group"><label className="input-label">Equipment</label>
              <input className="input" value={form.equipment} onChange={set('equipment')} placeholder="e.g. Barbell" /></div>
          </div>
          <div className="input-group"><label className="input-label">Primary Muscles (comma separated)</label>
            <input className="input" value={form.primary_muscles} onChange={set('primary_muscles')} placeholder="chest, triceps" /></div>
          <div className="input-group"><label className="input-label">Instructions (one per line)</label>
            <textarea className="input" rows={4} value={form.instructions} onChange={set('instructions')} /></div>
          <div className="input-group"><label className="input-label">Notes</label>
            <textarea className="input" rows={2} value={form.notes} onChange={set('notes')} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} disabled={loading}>
              {loading ? 'Saving...' : exercise ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Plans Tab ─────────────────────────────────────────────────────────────────

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanDesc, setNewPlanDesc] = useState('');

  const load = () => {
    exercisesAPI.getPlans().then(r => setPlans(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const loadPlan = async (id) => {
    const res = await exercisesAPI.getPlan(id);
    setSelected(res.data);
  };

  const createPlan = async () => {
    if (!newPlanName.trim()) { toast.error('Plan name required'); return; }
    try {
      await exercisesAPI.createPlan({ name: newPlanName, description: newPlanDesc, exercises: [] });
      toast.success('Plan created!');
      setShowCreate(false); setNewPlanName(''); setNewPlanDesc('');
      load();
    } catch { toast.error('Failed to create plan'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    await exercisesAPI.deletePlan(id);
    setPlans(p => p.filter(x => x.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success('Plan deleted');
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (selected) return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>← Back to Plans</button>
        <button className="btn btn-danger btn-sm" onClick={() => deletePlan(selected.id)}>Delete Plan</button>
      </div>
      <div className="card">
        <h2 style={{ marginBottom: 4 }}>{selected.name}</h2>
        {selected.description && <p style={{ color: 'var(--text-2)', marginBottom: 16 }}>{selected.description}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {selected.exercises?.length === 0 ? (
            <p style={{ color: 'var(--text-3)', fontSize: 14 }}>No exercises yet. Add from the Library tab.</p>
          ) : selected.exercises.map((ex, i) => (
            <div key={i} style={{ padding: '12px', background: 'var(--bg-3)', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{ex.exercise_name || ex.custom_exercise_name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
                  {[ex.target_sets && `${ex.target_sets} sets`, ex.target_reps && `${ex.target_reps} reps`, ex.target_weight && `${ex.target_weight}lbs`].filter(Boolean).join(' × ')}
                </div>
                {ex.primary_muscles?.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{ex.primary_muscles.join(', ')}</div>
                )}
              </div>
              <button
                className="btn btn-danger btn-sm"
                style={{ marginLeft: 8, flexShrink: 0 }}
                onClick={async () => {
                  try {
                    const updatedExercises = selected.exercises
                      .filter((_, j) => j !== i)
                      .map(e => ({
                        exercise_library_id: e.exercise_library_id,
                        custom_exercise_name: e.custom_exercise_name,
                        target_sets: e.target_sets,
                        target_reps: e.target_reps,
                        target_weight: e.target_weight,
                        notes: e.notes
                      }));
                    await exercisesAPI.updatePlan(selected.id, { name: selected.name, exercises: updatedExercises });
                    const res = await exercisesAPI.getPlan(selected.id);
                    setSelected(res.data);
                    load();
                    toast.success('Exercise removed');
                  } catch { toast.error('Failed to remove'); }
                }}
              >✕</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h3>My Workout Plans</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>+ New Plan</button>
      </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="input-group"><label className="input-label">Plan Name *</label>
              <input className="input" value={newPlanName} onChange={e => setNewPlanName(e.target.value)} placeholder="Push Day, Leg Day..." /></div>
            <div className="input-group"><label className="input-label">Description</label>
              <input className="input" value={newPlanDesc} onChange={e => setNewPlanDesc(e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={createPlan}>Create</button>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {plans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏋️</div>
          <div className="empty-state-title">No plans yet</div>
          <div className="empty-state-sub">Create a plan and add exercises from the Library</div>
        </div>
      ) : (
        <div className="grid-auto">
          {plans.map(plan => (
            <div key={plan.id} className="card" style={{ cursor: 'pointer' }} onClick={() => loadPlan(plan.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ marginBottom: 4 }}>{plan.name}</h3>
                  {plan.description && <p style={{ color: 'var(--text-2)', fontSize: 13 }}>{plan.description}</p>}
                  <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>{plan.exercise_count} exercises</p>
                </div>
                <button className="btn btn-danger btn-sm" style={{ flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); deletePlan(plan.id); }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Calendar Tab ──────────────────────────────────────────────────────────────

function CalendarTab() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [entries, setEntries] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [todaysWorkout, setTodaysWorkout] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCalendar = useCallback(() => {
    setLoading(true);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    Promise.all([
      exercisesAPI.getCalendar(year, month),
      exercisesAPI.getPlans(),
      exercisesAPI.getTodaysWorkout(),
    ]).then(([cal, pl, tw]) => {
      setEntries(cal.data);
      setPlans(pl.data);
      setTodaysWorkout(tw.data);
    }).finally(() => setLoading(false));
  }, [currentDate]);

  // Poll every 30 seconds for live sync with mobile app
  useEffect(() => {
    loadCalendar();
    const interval = setInterval(loadCalendar, 30000);
    return () => clearInterval(interval);
  }, [loadCalendar]);

  const entryForDate = (date) => {
    const ds = format(date, 'yyyy-MM-dd');
    return entries.find(e => e.scheduled_date === ds);
  };

  const schedule = async (planId) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    try {
      await exercisesAPI.scheduleWorkout({ plan_id: planId, scheduled_date: dateStr });
      toast.success('Workout scheduled!');
      loadCalendar();
    } catch { toast.error('Failed to schedule'); }
  };

  const remove = async (date) => {
    try {
      await exercisesAPI.removeFromCalendar(format(date, 'yyyy-MM-dd'));
      toast.success('Removed');
      loadCalendar();
    } catch { toast.error('Failed to remove'); }
  };

  const selectedEntry = entryForDate(selectedDate);
  const today = new Date();

  // Month days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart);
  const days = [];
  let d = calStart;
  while (d <= monthEnd || days.length % 7 !== 0) {
    days.push(new Date(d));
    d = addDays(d, 1);
  }

  // Week days
  const weekStart = startOfWeek(selectedDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
      <div>
        {/* Today's workout banner */}
        {todaysWorkout && (
          <div className="card" style={{ marginBottom: 16, background: 'var(--red-dim)', borderColor: 'var(--red)' }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 600, marginBottom: 4 }}>TODAY'S WORKOUT</div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{todaysWorkout.plan_name}</div>
              <div style={{ color: 'var(--text-2)', fontSize: 14 }}>{todaysWorkout.exercises?.length || 0} exercises</div>
            </div>
            {todaysWorkout.exercises?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {todaysWorkout.exercises.map((ex, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0',
                    borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ fontWeight: 500 }}>{ex.exercise_name || ex.custom_exercise_name}</span>
                    <span style={{ color: 'var(--text-3)' }}>
                      {[ex.target_sets && `${ex.target_sets}×${ex.target_reps || '?'}`,
                        ex.target_weight && `${ex.target_weight}lbs`].filter(Boolean).join(' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-full" onClick={async () => {
                try {
                  // Load today log, append exercises, save
                  const { logsAPI } = await import('../utils/api');
                  const today = new Date().toISOString().split('T')[0];
                  let currentLog = {};
                  let currentExercises = [];
                  try {
                    const res = await logsAPI.getLogByDate(today);
                    currentLog = res.data;
                    currentExercises = res.data.exercises || [];
                  } catch (e) {
                    currentLog = { log_date: today };
                  }
                  const newExercises = todaysWorkout.exercises.map(ex => ({
                    exercise_name: ex.exercise_name || ex.custom_exercise_name,
                    sets: ex.target_sets,
                    reps: ex.target_reps,
                    weight: ex.target_weight,
                  }));
                  await logsAPI.saveLog({ ...currentLog, log_date: today, exercises: [...currentExercises, ...newExercises] });
                  toast.success(`${newExercises.length} exercises added to today's log!`);
                } catch { toast.error('Failed to load exercises to log'); }
              }}>
                ▶ Load to Today's Log
              </button>
              <button className="btn btn-danger" onClick={() => remove(today)}>Remove</button>
            </div>
          </div>
        )}

        {/* View toggle + nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm"
              onClick={() => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1))}>←</button>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, letterSpacing: '0.05em' }}>
              {format(currentDate, 'MMMM yyyy').toUpperCase()}
            </span>
            <button className="btn btn-ghost btn-sm"
              onClick={() => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1))}>→</button>
          </div>
          <div className="tabs" style={{ padding: 3 }}>
            <button className={`tab ${view === 'month' ? 'active' : ''}`} onClick={() => setView('month')}>Month</button>
            <button className={`tab ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>Week</button>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: 'var(--bg-3)' }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} style={{ textAlign: 'center', padding: '10px 0', fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }}>{d}</div>
            ))}
          </div>

          {/* Calendar cells */}
          {view === 'month' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
              {days.map((day, i) => {
                const entry = entryForDate(day);
                const isToday = isSameDay(day, today);
                const isSelected = isSameDay(day, selectedDate);
                const inMonth = isSameMonth(day, currentDate);
                return (
                  <div key={i} onClick={() => setSelectedDate(day)}
                    style={{
                      minHeight: 64, padding: 6, border: '1px solid var(--border)',
                      cursor: 'pointer', background: isSelected ? 'var(--red-dim)' : isToday ? 'var(--bg-3)' : 'transparent',
                      borderColor: isSelected ? 'var(--red)' : 'var(--border)',
                      opacity: inMonth ? 1 : 0.3, transition: 'all var(--transition)'
                    }}>
                    <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--red)' : 'var(--text-2)', marginBottom: 4 }}>
                      {format(day, 'd')}
                    </div>
                    {entry && (
                      <div style={{ fontSize: 11, background: 'var(--red)', color: 'white',
                        borderRadius: 4, padding: '2px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        🏋️ {entry.plan_name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
              {weekDays.map((day, i) => {
                const entry = entryForDate(day);
                const isToday = isSameDay(day, today);
                const isSelected = isSameDay(day, selectedDate);
                return (
                  <div key={i} onClick={() => setSelectedDate(day)}
                    style={{ padding: 12, minHeight: 100, border: '1px solid var(--border)',
                      cursor: 'pointer', background: isSelected ? 'var(--red-dim)' : isToday ? 'var(--bg-3)' : 'transparent',
                      borderColor: isSelected ? 'var(--red)' : 'var(--border)' }}>
                    <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--red)' : 'var(--text-2)', marginBottom: 6 }}>
                      {format(day, 'd')}
                    </div>
                    {entry && (
                      <div style={{ fontSize: 11, background: 'var(--red)', color: 'white', borderRadius: 4, padding: '2px 4px' }}>
                        🏋️ {entry.plan_name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selected date panel */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
            {format(selectedDate, 'EEEE, MMM d')}
          </div>

          {selectedEntry ? (
            <>
              <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 4 }}>{selectedEntry.plan_name}</div>
              <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 12 }}>{selectedEntry.exercise_count} exercises</div>

              {selectedEntry.exercises?.map((ex, i) => (
                <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{ex.exercise_name || ex.custom_exercise_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    {[ex.target_sets && `${ex.target_sets} sets`, ex.target_reps && `${ex.target_reps} reps`, ex.target_weight && `${ex.target_weight}lbs`].filter(Boolean).join(' × ') || 'No targets set'}
                  </div>
                  {ex.primary_muscles?.length > 0 && (
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{ex.primary_muscles.join(', ')}</div>
                  )}
                </div>
              ))}

              <button className="btn btn-danger btn-full" style={{ marginTop: 12 }}
                onClick={() => remove(selectedDate)}>Remove from Calendar</button>
            </>
          ) : (
            <>
              <p style={{ color: 'var(--text-3)', fontSize: 14, marginBottom: 16 }}>No workout scheduled</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plans.map(plan => (
                  <button key={plan.id} className="btn btn-secondary btn-sm btn-full"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => schedule(plan.id)}>
                    + {plan.name}
                  </button>
                ))}
                {plans.length === 0 && <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No plans yet. Create one first.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    <Copyright />
    </div>
  );
}
